/**
 * Centralized filter configuration for ExperimentsTable
 * Handles all filter definitions, data extraction, and column rendering
 */

import React from 'react';
import TableFilterDropdown from '../shared/TableFilterDropdown';

/**
 * Extract unique values from table data for fallback when API data is not available
 */
const extractUniqueValues = {
  group: (data) => {
    const values = new Set();
    data.forEach(item => {
      if (item.store?.group) {
        values.add(item.store.group);
      }
    });
    return Array.from(values).sort();
  },

  location: (data) => {
    const values = new Set();
    data.forEach(item => {
      if (item.store?.location) {
        values.add(item.store.location);
      }
    });
    return Array.from(values).sort();
  },

  machineId: (data) => {
    const values = new Set();
    data.forEach(item => {
      if (item.store?.id) {
        values.add(item.store.id);
      }
    });
    return Array.from(values).sort();
  },

  products: (data) => {
    const values = new Set();
    data.forEach(item => {
      if (item.product?.original) {
        values.add(item.product.original);
      }
      if (item.product?.new) {
        values.add(item.product.new);
      }
    });
    return Array.from(values).sort();
  },

  type: (data) => {
    const values = new Set();
    data.forEach(item => {
      if (item.type) {
        values.add(item.type);
      }
    });
    return Array.from(values).sort();
  },

  status: (data) => {
    const values = new Set();
    data.forEach(item => {
      if (item.status) {
        values.add(item.status);
      }
    });
    return Array.from(values).sort();
  },

  tags: (data) => {
    const values = new Set();
    data.forEach(item => {
      if (item.tags && Array.isArray(item.tags)) {
        item.tags.forEach(tag => {
          if (tag) {
            values.add(tag);
          }
        });
      }
    });
    return Array.from(values).sort();
  },

  route: (data) => {
    const map = new Map();
    data.forEach(item => {
      if (Array.isArray(item.routes)) {
        item.routes.forEach(route => {
          if (route && route.id != null) {
            const key = String(route.id);
            if (!map.has(key)) {
              map.set(key, {
                id: route.id,
                name: route.name || route.code || key
              });
            }
          }
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }
};

/**
 * Get consolidated filter values - uses API data when available, fallback to local extraction
 */
export const getFilterValues = (filterData, data) => {
  const filters = {};

  // Build a reverse map from extractKey → apiKey using FILTER_CONFIG
  const apiKeyMap = {};
  Object.values(FILTER_CONFIG).forEach(config => {
    apiKeyMap[config.extractKey] = config.apiKey;
  });

  Object.keys(extractUniqueValues).forEach(key => {
    const apiKey = apiKeyMap[key] || key;
    filters[key] = filterData?.[apiKey] || extractUniqueValues[key](data);
  });

  return filters;
};

/**
 * Create a filter dropdown renderer for a specific filter type
 */
export const createFilterDropdown = (filterKey, filterValues, columnName, filtersLoading, t) => {
  return (props) => {
    // Special handling for products which are now objects with {id, name}
    let values;
    if ((filterKey === 'products' || filterKey === 'route') && filterValues[filterKey].length > 0 && typeof filterValues[filterKey][0] === 'object') {
      values = filterValues[filterKey].map(item => ({ 
        text: item.name, 
        value: String(item.id ?? item.name) 
      }));
    } else {
      // Regular string values
      values = filterValues[filterKey].map(value => ({ text: value, value }));
    }
    
    return (
      <TableFilterDropdown
        type="checkbox"
        values={values}
        columnName={columnName}
        loading={filtersLoading}
        {...props}
      />
    );
  };
};

/**
 * Filter configuration mapping
 * Defines the relationship between filter keys and their API/data field mappings
 */
export const FILTER_CONFIG = {
  group: {
    apiKey: 'group',
    extractKey: 'group'
  },
  location: {
    apiKey: 'location', 
    extractKey: 'location'
  },
  storeId: {
    apiKey: 'machineId',
    extractKey: 'machineId'
  },
  products: {
    apiKey: 'products',
    extractKey: 'products'
  },
  type: {
    apiKey: 'type',
    extractKey: 'type'
  },
  status: {
    apiKey: 'status',
    extractKey: 'status'
  },
  tags: {
    apiKey: 'tags',
    extractKey: 'tags'
  },
  route: {
    apiKey: 'routes',
    extractKey: 'route'
  }
};
